<template>
  <div class="booking-form-price">
    <v-alert
      v-if="hasError"
      dense
      text
      type="error"
      class="d-block status-alert ml-4"
    >
      <div class="d-flex">
        <div class="flex-grow-1 my-auto">Ett fel inträffade</div>
      </div>
    </v-alert>
    <div
      v-else-if="!isExistingBooking"
      class="pl-4 d-flex align-center"
      :class="{ 'd-block py-2': isMobile }"
    >
      <span class="text-h6">Kostnad:</span>
      <v-progress-circular
        v-if="loading"
        indeterminate
        size="24"
        class="ml-6"
      ></v-progress-circular>
      <span v-else class="text-h6 pl-6" v-text="chargeTotalFormatted"></span>
    </div>
    <div
      v-else
      class="pl-4 d-flex align-center"
      :class="{ 'd-block py-2': isMobile }"
    >
      <div class="d-flex flex-column">
        <v-progress-circular
          v-if="loading"
          indeterminate
          size="24"
          class="ml-6"
        ></v-progress-circular>
        <div v-else :class="{ 'text-h6': !dense, 'text-subtitle-2': dense }">
          {{ formatAmount(currentTotal) }}
        </div>
      </div>
      <v-icon class="mx-4">mdi-chevron-right</v-icon>
      <div class="d-flex" style="width: 340px;">
        <v-select
          v-model="inputRebookingOption"
          return-object
          item-value="level"
          :loading="loading"
          :disabled="loading"
          :items="rebookingOptions"
          hide-details
          outlined
          label="Nytt pris"
          :dense="dense"
        >
          <template v-slot:selection="data">
            <div class="d-flex">
              <div class="my-auto">
                <v-icon
                  class="mr-4"
                  :color="rebookingOptionColor(data.item)"
                >
                  {{ rebookingOptionIcon(data.item) }}
                </v-icon>
              </div>
              <div class="my-auto d-flex flex-column">
                <div class="text-nowrap" :class="{ 'text-body-1': !dense, 'text-body-2': dense }">{{ data.item.title }}</div>
                <div class="text-subtitle-2">{{ formatAmount(data.item.total) }}</div>
              </div>
            </div>
          </template>
          <template v-slot:item="data">
            <div class="d-flex py-2 full-width">
              <div class="my-auto">
                <v-icon
                  class="mr-4"
                  :color="rebookingOptionColor(data.item)"
                >
                  {{ rebookingOptionIcon(data.item) }}
                </v-icon>
              </div>
              <div class="my-auto d-flex flex-column">
                <div class="text-body-1">{{ data.item.title }}</div>
                <div class="text-subtitle-2">{{ formatAmount(data.item.total) }}</div>
              </div>
              <div class="my-auto flex-grow-1 text-right">
                <div v-show="data.item.is_default" class="text-caption success--text pl-4">Enligt ombokningsvillkor</div>
              </div>
            </div>
          </template>
        </v-select>
        <v-menu
          right
          transition="scale-transition"
          origin="left"
          max-height="600"
          :close-on-content-click="isMobile"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              icon
              class="my-auto ml-3"
            >
              <v-icon color="info">mdi-information</v-icon>
            </v-btn>
          </template>
          <v-card max-width="500">
            <v-card-text class="py-2">
              <div
                v-for="item in rebookingOptionInformationItems"
                :key="item.level"
                class="d-flex py-2"
              >
                <div class="mt-1">
                  <v-icon
                    class="mr-4"
                    :color="item.color"
                    large
                  >
                    {{ item.icon }}
                  </v-icon>
                </div>
                <div class="my-auto d-flex flex-column">
                  <div class="text-subtitle-1 font-weight-medium">{{ item.title }}</div>
                  <div class="text-body-2">{{ item.description }}</div>
                  <div v-if="item.deadlineText" class="text-caption mt-1 font-italic">{{ item.deadlineText }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-menu>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .booking-form-price {
    .v-input__append-inner {
      margin-top: auto !important;
      margin-bottom: auto !important;
    }
  }
</style>

<script>
const REBOOKING_OPTION_INFORMATION_ITEMS = [
  {
    level: 'free',
    title: 'Fri ombokning',
    description: 'Gästen kan fritt ändra in- och utcheckningsdatum, antal personer och platsval och betalar alltid det nya priset.'
  },
  {
    level: 'limited',
    title: 'Begränsad ombokning',
    description: 'Gästen kan ändra in- och utcheckningsdatum, antal personer och platsval men slutpriset vid ombokning kan inte understiga det ursprungliga priset före ombokningen.'
  },
  {
    level: 'restrictive',
    title: 'Restriktiv ombokning',
    description: 'Ombokningen kan inte resultera i ett lägre dygnspris. Nätter som inte längre ingår i bokningsperioden återbetalas inte. Alla nätter som tillkommer debiteras med det nya dygnspriset.'
  },
  {
    level: 'keep_current_position_fees',
    title: 'Behåll nuvarande platsavgifter',
    description: 'Genomför ombokning utan att automatiskt ändra i bokningens platsavgifter.'
  }
]

export default {
  name: 'BookingFormPrice',
  props: {
    isExistingBooking: Boolean,
    loading: Boolean,
    chargeTotal: {
      type: Number,
      default: 0
    },
    isMobile: Boolean,
    rebookingPreview: Object,
    selectedRebookingOption: Object,
    hasError: Boolean,
    dense: Boolean
  },
  computed: {
    inputRebookingOption: {
      get: function () {
        return this.selectedRebookingOption
      },
      set: function (val) {
        this.$emit('select-rebooking-option', val)
      }
    },
    currentTotal: function () {
      // pris före ändring när redigerar bokning
      return this.rebookingPreview?.current_total ?? 0
    },
    rebookingOptions: function () {
      return this.rebookingPreview?.rebooking_options ?? []
    },
    rebookingOptionInformationItems: function () {
      return REBOOKING_OPTION_INFORMATION_ITEMS.map(item => ({
        ...item,
        icon: this.rebookingOptionIcon(item),
        color: this.rebookingOptionColor(item),
        deadlineText: this.rebookingOptions.find(option => option.level === item.level)?.deadline_description ?? ''
      }))
    },
    chargeTotalFormatted: function () {
      return this.chargeTotal === 0 ? 'Inget pris' : `${this.chargeTotal / 100} kr`
    }
  },
  methods: {
    formatAmount: function (amount) {
      if (amount === null || amount === undefined) {
        return 'N/A'
      }
      return window.conversion.toHumanReadableAmount(amount / 100)
    },
    rebookingOptionIcon: function (option) {
      if (option?.level === 'free') {
        return 'mdi-sync'
      }

      if (option?.level === 'limited') {
        return 'mdi-sync-alert'
      }

      if (option?.level === 'restrictive') {
        return 'mdi-sync-off'
      }

      return 'mdi-lock-outline'
    },
    rebookingOptionColor: function (option) {
      if (option?.level === 'free') {
        return 'success'
      }

      if (option?.level === 'limited') {
        return 'warning'
      }

      if (option?.level === 'restrictive') {
        return 'error'
      }

      return 'primary'
    }
  }
}

</script>
